import { Attachment } from '@/modules/Upload/types'
import React from 'react'
import { Typogaphy } from 'vnetwork-ui'
import ErrorBoundary from '@/components/ErrorBoundary'
import { useFilePreviewWithPresigned } from '@/hooks/useFilePreviewWithPresigned'

interface AttachmentCardProps {
    attachment: Attachment
}

const AttachmentCard: React.FC<AttachmentCardProps> = ({ attachment }) => {
    const { openFilePreview } = useFilePreviewWithPresigned()
    const handlePreview = async (attachment: Attachment) => {
        await openFilePreview(attachment)
    }
    // Helper function to format file size
    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 B'
        const k = 1024
        const sizes = ['B', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
    }

    // Get file extension for icon
    const getFileExtension = (fileName: string) => {
        return fileName?.split('.').pop()?.toLowerCase() || ''
    }

    // download in new tab
    const handleDownload = () => {
        const downloadUrl = `${import.meta.env.APP_STORAGE}/${attachment.attachment_file_url}`
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = attachment.attachment_file_name
        link.target = '_blank'
        link.click()
    }

    const extension = getFileExtension(attachment.attachment_file_name)
    const isPDF = extension === 'pdf'

    return (
        <ErrorBoundary>
            <div
                className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => handlePreview(attachment)}
            >
                {/* File Icon */}
                <div className="flex-shrink-0">
                    {isPDF ? (
                        <div className="w-10 h-10 bg-red-100 rounded flex items-center justify-center">
                            <svg
                                className="w-6 h-6 text-red-600"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                    ) : (
                        <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center">
                            <svg
                                className="w-6 h-6 text-blue-600"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                    )}
                </div>

                {/* File Info */}
                <div className="flex-1 min-w-0">
                    <Typogaphy className="font-medium text-sm text-gray-900 truncate leading-relaxed">
                        {attachment.attachment_file_name}
                    </Typogaphy>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>
                            {formatFileSize(attachment.attachment_file_size)}
                        </span>
                        <span>•</span>
                        <Typogaphy
                            onClick={(e) => {
                                e.stopPropagation()
                                handleDownload()
                            }}
                            className="text-gray-500 transition-colors cursor-pointer hover:underline hover:text-primary"
                        >
                            Download
                        </Typogaphy>
                    </div>
                </div>
            </div>
        </ErrorBoundary>
    )
}

export default AttachmentCard
